// The platform: a browser, the iOS store app or the Android store app
// (mobile-apps.md §4, R2927, R2936).
//
// The iOS app shell injects `window.apShell` before the page script runs.
// The Android TWA opens the start URL with `?shell=android`, and Chrome
// gives the first page the `android-app://` referrer of the app id. A
// later page of the same tab sees neither, so the answer stays in
// `sessionStorage`. It does not go into `localStorage`, because Chrome
// shares that storage between the TWA and its own tabs.
//
// The page decides once, at its boot (`bootPlatform` of `platform/boot.ts`),
// and every later call reads that answer. The decision takes the `shell`
// query off the address, so a link that a player shares does not carry it.
//
// The store-app behaviour follows this runtime fact. It is no feature
// flag (mobile-apps.md §2).

import { ANDROID_REFERRER, ANDROID_START_PARAM, ANDROID_START_VALUE, SHELL_GLOBAL } from '../gen/mobile';

/** A store app: the iOS app shell or the Android TWA. */
export type StoreApp = 'ios' | 'android';

/** The `sessionStorage` key that remembers the Android store app. */
export const SHELL_MEMORY_KEY = 'ap.shell';

/** The parts of the window that the decision reads. A test passes a
 *  plain object. Each access can throw in a page with blocked storage, so
 *  every read stands in a `try`. */
export interface PlatformWindow {
  readonly [SHELL_GLOBAL]?: unknown;
  readonly location: { readonly href: string };
  readonly history?: { readonly state: unknown; replaceState(data: unknown, unused: string, url?: string): void };
  readonly document?: { readonly referrer: string };
  readonly sessionStorage?: Pick<Storage, 'getItem' | 'setItem'>;
}

/** The session storage of the window, or null where the access throws. */
function sessionOf(w: PlatformWindow): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return w.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** True when this tab remembers the Android store app. */
function remembered(w: PlatformWindow): boolean {
  try {
    return sessionOf(w)?.getItem(SHELL_MEMORY_KEY) === ANDROID_START_VALUE;
  } catch {
    return false;
  }
}

/** Remembers the Android store app for this tab. */
function remember(w: PlatformWindow): void {
  try {
    sessionOf(w)?.setItem(SHELL_MEMORY_KEY, ANDROID_START_VALUE);
  } catch { /* a blocked storage keeps no copy */ }
}

/** True when the start query names the Android store app. The query
 *  comes off the address in either case: the key is the store app's own. */
function takeStartQuery(w: PlatformWindow): boolean {
  let url: URL;
  try {
    url = new URL(w.location.href);
  } catch {
    return false;
  }
  if (!url.searchParams.has(ANDROID_START_PARAM)) return false;
  const android = url.searchParams.get(ANDROID_START_PARAM) === ANDROID_START_VALUE;
  url.searchParams.delete(ANDROID_START_PARAM);
  try {
    w.history?.replaceState(w.history.state, '', url.toString());
  } catch { /* the address keeps the query */ }
  return android;
}

/** The referrer of the page, or '' where the read throws. */
function referrerOf(w: PlatformWindow): string {
  try {
    return w.document?.referrer ?? '';
  } catch {
    return '';
  }
}

/**
 * The platform of the page in `w`. The iOS global wins. The Android
 * answer comes from the start query, the referrer or the memory of the
 * tab, and a yes is written to the memory.
 */
export function detectStoreApp(w: PlatformWindow): StoreApp | null {
  const shell = w[SHELL_GLOBAL];
  if (typeof shell === 'object' && shell !== null) return 'ios';
  const query = takeStartQuery(w);
  // A prefix match: some Chrome versions add a trailing `/` to the
  // referrer. An App Link opens its own URL with no start query, so the
  // referrer is the one sign there.
  const android = query || referrerOf(w).startsWith(ANDROID_REFERRER) || remembered(w);
  if (android) remember(w);
  return android ? 'android' : null;
}

/** The decided platform; undefined until the first read. */
let decided: StoreApp | null | undefined;

/**
 * The store app this page runs in, or null for a browser or a home-screen
 * web app. The first call decides from the window; every later call reads
 * that answer.
 */
export function storeApp(): StoreApp | null {
  if (decided === undefined) {
    decided = typeof window === 'undefined' ? null : detectStoreApp(window as unknown as PlatformWindow);
  }
  return decided;
}

/** Sets the decided platform, or undefined to decide again. Tests call it. */
export function setStoreAppForTests(app: StoreApp | null | undefined): void {
  decided = app;
}

/** True in a store app: the page shows no shop, no price and no buy link
 *  (mobile-apps.md §8, R2927). */
export function sellsNothing(): boolean {
  return storeApp() !== null;
}

/** True in a store app: the page hides the casino (mobile-apps.md §8). */
export function casinoHidden(): boolean {
  return storeApp() !== null;
}
